<!-- eslint-disable -->
<template>
  <div class="chat-window" :style="{ right: `${position}px` }">
    <!-- Header -->
    <div class="chat-window-header" @click="$emit('toggle-minimize')">
      <div class="d-flex align-center flex-grow-1">
        <v-avatar size="32" color="primary" class="mr-2">
          <v-img v-if="conversation.otherUser?.avatarUrl" :src="conversation.otherUser.avatarUrl"></v-img>
          <span v-else>{{ conversation.otherUser?.displayName?.charAt(0) || '?' }}</span>
        </v-avatar>
        <div class="header-info">
          <div class="header-name">{{ conversation.otherUser?.displayName }}</div>
          <div class="header-status">
            <span v-if="isTyping" class="typing-text">{{ $t('chat.typing') }}</span>
            <span v-else-if="onlineStatus === 'online'" class="status-online">{{ $t('profile.online') }}</span>
            <span v-else-if="onlineStatus === 'away'" class="status-away">{{ $t('profile.away') }}</span>
            <span v-else class="status-offline">{{ $t('profile.offline') }}</span>
          </div>
        </div>
      </div>
      <div class="header-actions">
        <v-btn icon size="x-small" variant="text" @click.stop="expanded = true" :title="$t('chat.expand') || 'Forstør'">
          <v-icon size="18">mdi-arrow-expand</v-icon>
        </v-btn>
        <v-btn icon size="x-small" variant="text" @click.stop="startVideoCall" :title="$t('chat.videoCall')">
          <v-icon size="18">mdi-video</v-icon>
        </v-btn>
        <v-btn icon size="x-small" variant="text" @click.stop="$emit('minimize')">
          <v-icon size="18">mdi-minus</v-icon>
        </v-btn>
        <v-btn icon size="x-small" variant="text" @click.stop="$emit('close')">
          <v-icon size="18">mdi-close</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Messages area (hidden when minimized) -->
    <template v-if="!minimized">
      <div 
          class="chat-window-messages" 
          ref="messagesContainer"
          @dragover.prevent="onDragOver"
          @dragleave="onDragLeave"
          @drop.prevent="onDrop"
          :class="{ 'drop-active': isDragging }"
        >
        <div v-if="loading" class="text-center py-4">
          <v-progress-circular indeterminate size="24" color="primary"></v-progress-circular>
        </div>
        
        <div v-if="hasMore && !loading" class="text-center py-2">
          <v-btn variant="text" size="small" @click="loadMore" :loading="loadingMore">
            {{ $t('chat.loadMore') }}
          </v-btn>
        </div>

        <div
          v-for="(message, index) in messages"
          :key="message.messageId"
          class="message-wrapper"
          :class="{ 'own-message': message.senderId === currentUserId }"
          :data-message-id="message.messageId"
        >
          <!-- Date separator -->
          <div v-if="showDateSeparator(index)" class="date-separator">
            {{ formatDate(message.createdAt) }}
          </div>

          <div class="message-bubble" :class="{ deleted: message.isDeleted }">
            <!-- Reply quote -->
            <div 
              v-if="message.replyTo" 
              class="reply-quote"
              @click="scrollToMessage(message.replyTo.messageId)"
            >
              <div class="reply-quote-name">{{ message.replyTo.senderName }}</div>
              <div class="reply-quote-text">
                {{ message.replyTo.messageType === 'image' ? '📷 ' + $t('chat.image') : message.replyTo.content }}
              </div>
            </div>

            <!-- Image -->
            <img 
              v-if="message.file && message.messageType === 'image' && !message.isDeleted" 
              :src="message.file.url" 
              class="message-image"
              @click="$emit('preview-image', message.file.url)"
            />
            
            <!-- File -->
            <div v-else-if="message.file && !message.isDeleted" class="file-attachment" @click="openFile(message.file.url)">
              <v-icon size="small" class="mr-1">mdi-file-document</v-icon>
              {{ message.file.name }}
            </div>

            <!-- Video call invite -->
            <div v-if="isVideoCallMessage(message) && !message.isDeleted" class="video-call-invite">
              <v-icon color="primary" class="mr-2">mdi-video</v-icon>
              <span class="video-call-label">{{ $t('chat.videoCall') }}</span>
              <v-btn
                v-if="message.senderId !== currentUserId"
                color="success"
                size="small"
                variant="flat"
                class="ml-auto"
                @click="joinVideoCall(message)"
              >
                <v-icon start size="small">mdi-phone</v-icon>
                {{ $t('chat.joinCall') }}
              </v-btn>
            </div>

            <!-- Text content -->
            <div v-if="!isVideoCallMessage(message)" class="message-text" v-html="formatInline(message.content)"></div>
            
            <!-- Reactions display -->
            <div v-if="message.reactions && Object.keys(message.reactions).length > 0" class="message-reactions">
              <span 
                v-for="(count, reaction) in message.reactions" 
                :key="reaction" 
                class="reaction-badge"
                :class="{ 'user-reacted': message.userReaction === reaction }"
                @click="toggleReaction(message, reaction)"
              >
                {{ getReactionEmoji(reaction) }} {{ count }}
              </span>
            </div>
            
            <!-- Meta -->
            <div class="message-meta">
              <span class="message-time">{{ formatTime(message.createdAt) }}</span>
              <span v-if="message.isEdited && !message.isDeleted" class="edited-badge">({{ $t('chat.edited') }})</span>
              <v-icon 
                v-if="message.senderId === currentUserId && !message.isDeleted"
                size="12"
                :color="message.readAt ? 'primary' : 'grey'"
                class="ml-1"
              >
                {{ message.readAt ? 'mdi-check-all' : 'mdi-check' }}
              </v-icon>
            </div>

            <!-- Reaction picker + Reply button -->
            <div v-if="!message.isDeleted" class="reaction-picker">
              <button 
                v-for="r in reactionOptions" 
                :key="r.key"
                class="reaction-btn"
                :class="{ active: message.userReaction === r.key }"
                @click="toggleReaction(message, r.key)"
                :title="r.label"
              >
                {{ r.emoji }}
              </button>
              <button 
                class="reaction-btn reply-btn"
                @click="startReply(message)"
                :title="$t('chat.reply')"
              >
                ↩️
              </button>
            </div>

            <!-- Actions for own messages -->
            <div v-if="message.senderId === currentUserId && !message.isDeleted" class="message-actions">
              <v-btn icon size="x-small" variant="text" @click="startEdit(message)">
                <v-icon size="14">mdi-pencil</v-icon>
              </v-btn>
              <v-btn icon size="x-small" variant="text" @click="deleteMessage(message)">
                <v-icon size="14">mdi-delete</v-icon>
              </v-btn>
            </div>
          </div>
        </div>
      </div>

      <!-- Input area -->
      <div class="chat-window-input">
        <!-- Paste preview -->
        <div v-if="pastePreview" class="paste-preview">
          <img v-if="pastePreview.type === 'image'" :src="pastePreview.dataUrl" class="paste-preview-image" />
          <div v-else class="paste-preview-file">
            <v-icon size="small" class="mr-1">mdi-file-document</v-icon>
            {{ pastePreview.name }}
          </div>
          <v-btn icon size="x-small" variant="text" @click="clearPastePreview" class="paste-preview-close">
            <v-icon size="14">mdi-close</v-icon>
          </v-btn>
        </div>

        <!-- Edit indicator -->
        <div v-if="editingMessage" class="edit-indicator">
          <v-icon size="small">mdi-pencil</v-icon>
          <span>{{ $t('chat.editingMessage') }}</span>
          <v-btn icon size="x-small" variant="text" @click="cancelEdit">
            <v-icon size="14">mdi-close</v-icon>
          </v-btn>
        </div>

        <!-- Reply preview -->
        <div v-if="replyingTo" class="reply-preview">
          <div class="reply-preview-content">
            <div class="reply-preview-name">
              {{ replyingTo.sender?.displayName || replyingTo.senderName || $t('chat.reply') }}
            </div>
            <div class="reply-preview-text">
              {{ replyingTo.messageType === 'image' ? '📷 ' + $t('chat.image') : replyingTo.content }}
            </div>
          </div>
          <v-btn icon size="x-small" variant="text" @click="cancelReply">
            <v-icon size="14">mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="input-row">
          <v-btn icon size="small" variant="text" @click="triggerFileUpload">
            <v-icon>mdi-paperclip</v-icon>
          </v-btn>
          <input 
            ref="fileInput" 
            type="file" 
            accept="image/*,video/mp4,video/webm,.pdf,.doc,.docx,.txt" 
            style="display: none;" 
            @change="handleFileUpload"
          />
          
          <v-textarea
            v-model="newMessage"
            :placeholder="$t('chat.typeMessage')"
            variant="plain"
            rows="1"
            max-rows="3"
            auto-grow
            hide-details
            density="compact"
            class="message-input"
            @keydown="handleKeydown"
            @input="sendTypingIndicator"
            @paste="handlePaste"
          ></v-textarea>
          
          <v-btn icon size="small" color="primary" @click="sendMessage" :loading="sending" :disabled="!canSend">
            <v-icon>mdi-send</v-icon>
          </v-btn>
        </div>
        
        <v-progress-linear v-if="uploading" indeterminate color="primary" height="2"></v-progress-linear>
      </div>
    </template>

    <!-- ═══════════════════════════════════════════════════════ -->
    <!-- EXPANDED DIALOG (large mode for visually impaired)     -->
    <!-- ═══════════════════════════════════════════════════════ -->
    <v-dialog v-model="expanded" max-width="800" persistent scrollable>
      <v-card class="expanded-chat">
        <!-- Expanded header -->
        <v-card-title class="expanded-header">
          <div class="d-flex align-center flex-grow-1">
            <v-avatar size="48" color="primary" class="mr-3">
              <v-img v-if="conversation.otherUser?.avatarUrl" :src="conversation.otherUser.avatarUrl"></v-img>
              <span v-else class="text-h6">{{ conversation.otherUser?.displayName?.charAt(0) || '?' }}</span>
            </v-avatar>
            <div>
              <div class="expanded-header-name">{{ conversation.otherUser?.displayName }}</div>
              <div class="expanded-header-status">
                <span v-if="isTyping" class="typing-text">{{ $t('chat.typing') }}...</span>
                <span v-else-if="onlineStatus === 'online'" class="status-online">● {{ $t('profile.online') }}</span>
                <span v-else-if="onlineStatus === 'away'" class="status-away">● {{ $t('profile.away') }}</span>
                <span v-else class="status-offline">● {{ $t('profile.offline') }}</span>
              </div>
            </div>
          </div>
          <v-btn icon variant="text" @click="expanded = false" :title="$t('chat.collapse') || 'Formindsk'">
            <v-icon>mdi-arrow-collapse</v-icon>
          </v-btn>
        </v-card-title>

        <!-- Expanded messages -->
        <v-card-text 
            class="expanded-messages" 
            ref="expandedMessagesContainer"
            @dragover.prevent="onDragOver"
            @dragleave="onDragLeave"
            @drop.prevent="onDrop"
            :class="{ 'drop-active': isDragging }"
          >
          <div v-if="loading" class="text-center py-8">
            <v-progress-circular indeterminate size="40" color="primary"></v-progress-circular>
          </div>

          <div v-if="hasMore && !loading" class="text-center py-3">
            <v-btn variant="outlined" @click="loadMore" :loading="loadingMore">
              {{ $t('chat.loadMore') }}
            </v-btn>
          </div>

          <div
            v-for="(message, index) in messages"
            :key="'exp-' + message.messageId"
            class="expanded-message-wrapper"
            :class="{ 'own-message': message.senderId === currentUserId }"
            :data-message-id="'exp-' + message.messageId"
          >
            <!-- Date separator -->
            <div v-if="showDateSeparator(index)" class="expanded-date-separator">
              {{ formatDate(message.createdAt) }}
            </div>

            <div class="expanded-bubble" :class="{ deleted: message.isDeleted }">
              <!-- Reply quote -->
              <div v-if="message.replyTo" class="expanded-reply-quote">
                <div class="reply-quote-name">{{ message.replyTo.senderName }}</div>
                <div class="reply-quote-text">
                  {{ message.replyTo.messageType === 'image' ? '📷 ' + $t('chat.image') : message.replyTo.content }}
                </div>
              </div>

              <!-- Image -->
              <img 
                v-if="message.file && message.messageType === 'image' && !message.isDeleted" 
                :src="message.file.url" 
                class="expanded-message-image"
                @click="$emit('preview-image', message.file.url)"
              />

              <!-- File -->
              <div v-else-if="message.file && !message.isDeleted" class="expanded-file-attachment" @click="openFile(message.file.url)">
                <v-icon class="mr-2">mdi-file-document</v-icon>
                {{ message.file.name }}
              </div>

              <!-- Video call -->
              <div v-if="isVideoCallMessage(message) && !message.isDeleted" class="video-call-invite expanded-video-call">
                <v-icon color="primary" class="mr-2" size="28">mdi-video</v-icon>
                <span class="video-call-label">{{ $t('chat.videoCall') }}</span>
                <v-btn
                  v-if="message.senderId !== currentUserId"
                  color="success"
                  variant="flat"
                  class="ml-auto"
                  @click="joinVideoCall(message)"
                >
                  <v-icon start>mdi-phone</v-icon>
                  {{ $t('chat.joinCall') }}
                </v-btn>
              </div>

              <!-- Text -->
              <div v-if="!isVideoCallMessage(message)" class="expanded-message-text" v-html="formatInline(message.content)"></div>

              <!-- Reactions -->
              <div v-if="message.reactions && Object.keys(message.reactions).length > 0" class="expanded-reactions">
                <span 
                  v-for="(count, reaction) in message.reactions" 
                  :key="reaction" 
                  class="expanded-reaction-badge"
                  :class="{ 'user-reacted': message.userReaction === reaction }"
                  @click="toggleReaction(message, reaction)"
                >
                  {{ getReactionEmoji(reaction) }} {{ count }}
                </span>
              </div>

              <!-- Meta -->
              <div class="expanded-message-meta">
                <span>{{ formatTime(message.createdAt) }}</span>
                <span v-if="message.isEdited && !message.isDeleted">({{ $t('chat.edited') }})</span>
                <v-icon 
                  v-if="message.senderId === currentUserId && !message.isDeleted"
                  size="16"
                  :color="message.readAt ? 'primary' : 'grey'"
                  class="ml-1"
                >
                  {{ message.readAt ? 'mdi-check-all' : 'mdi-check' }}
                </v-icon>
              </div>

              <!-- Expanded reaction picker -->
              <div v-if="!message.isDeleted" class="expanded-reaction-picker">
                <button 
                  v-for="r in reactionOptions" 
                  :key="r.key"
                  class="expanded-react-btn"
                  :class="{ active: message.userReaction === r.key }"
                  @click="toggleReaction(message, r.key)"
                  :title="r.label"
                >
                  {{ r.emoji }}
                </button>
                <button 
                  class="expanded-react-btn reply-btn"
                  @click="startReply(message)"
                  :title="$t('chat.reply')"
                >
                  ↩️
                </button>
              </div>
            </div>
          </div>
        </v-card-text>

        <!-- Expanded input -->
        <v-card-actions class="expanded-input-area">
          <!-- Paste preview in expanded -->
          <div v-if="pastePreview" class="paste-preview expanded-paste-preview">
            <img v-if="pastePreview.type === 'image'" :src="pastePreview.dataUrl" class="paste-preview-image-lg" />
            <div v-else class="paste-preview-file">
              <v-icon class="mr-2">mdi-file-document</v-icon>
              {{ pastePreview.name }}
            </div>
            <v-btn icon size="small" variant="text" @click="clearPastePreview">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>

          <!-- Reply preview in expanded -->
          <div v-if="replyingTo" class="reply-preview expanded-reply-preview">
            <div class="reply-preview-content">
              <div class="reply-preview-name">
                {{ replyingTo.sender?.displayName || replyingTo.senderName || $t('chat.reply') }}
              </div>
              <div class="reply-preview-text">
                {{ replyingTo.messageType === 'image' ? '📷 ' + $t('chat.image') : replyingTo.content }}
              </div>
            </div>
            <v-btn icon size="small" variant="text" @click="cancelReply">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>

          <div class="expanded-input-row">
            <v-btn icon variant="text" @click="triggerFileUpload" size="large">
              <v-icon size="28">mdi-paperclip</v-icon>
            </v-btn>

            <v-textarea
              v-model="newMessage"
              :placeholder="$t('chat.typeMessage')"
              variant="outlined"
              rows="2"
              max-rows="5"
              auto-grow
              hide-details
              density="comfortable"
              class="expanded-message-input"
              @keydown="handleKeydown"
              @input="sendTypingIndicator"
              @paste="handlePaste"
            ></v-textarea>

            <v-btn icon color="primary" @click="sendMessage" :loading="sending" :disabled="!canSend" size="large">
              <v-icon size="28">mdi-send</v-icon>
            </v-btn>
          </div>

          <v-progress-linear v-if="uploading" indeterminate color="primary" height="3"></v-progress-linear>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Video Call Dialog -->
    <VideoCall
      v-if="videoCallChannel"
      v-model="showVideoCall"
      :channel-name="videoCallChannel"
      :other-user="conversation.otherUser"
      :is-outgoing="isOutgoingCall"
      :app-id="agoraAppId"
      @call-ended="onCallEnded"
    />
  </div>
</template>

<script setup>
/* eslint-disable */
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import api from '../services/api'
import VideoCall from './VideoCall.vue'

const agoraAppId = process.env.VUE_APP_AGORA_APP_ID || 'YOUR_AGORA_APP_ID'

const props = defineProps({
  conversation: { type: Object, required: true },
  position: { type: Number, default: 20 },
  minimized: { type: Boolean, default: false }
})

const emit = defineEmits(['close', 'minimize', 'toggle-minimize', 'preview-image', 'update-unread', 'new-message', 'online-status-change'])

const store = useStore()
const { t } = useI18n()

import { notificationSound } from '../services/notificationSound'
import { formatInline } from '../utils/formatText'

const messages = ref([])
const loading = ref(true)
const loadingMore = ref(false)
const hasMore = ref(false)
const newMessage = ref('')
const sending = ref(false)
const uploading = ref(false)
const editingMessage = ref(null)
const replyingTo = ref(null)
const isTyping = ref(false)
const onlineStatus = ref('offline')
const expanded = ref(false)

// Paste preview state
const pastePreview = ref(null)
const pasteFile = ref(null)

// Video call state
const showVideoCall = ref(false)
const videoCallChannel = ref('')
const isOutgoingCall = ref(true)

// Reaction options
const reactionOptions = [
  { key: 'heart', emoji: '❤️', label: 'Heart' },
  { key: 'like', emoji: '👍', label: 'Like' },
  { key: 'smile', emoji: '😊', label: 'Smile' },
  { key: 'lol', emoji: '😂', label: 'LOL' }
]

const getReactionEmoji = (key) => {
  const r = reactionOptions.find(o => o.key === key)
  return r ? r.emoji : key
}

// Can send if there's text OR a pasted file
const canSend = computed(() => {
  return (newMessage.value && newMessage.value.trim()) || pasteFile.value
})

const toggleReaction = async (message, reactionKey) => {
  try {
    if (message.userReaction === reactionKey) {
      await api.delete(`/messages/${message.messageId}/reactions`)
      message.userReaction = null
      if (message.reactions[reactionKey]) {
        message.reactions[reactionKey]--
        if (message.reactions[reactionKey] <= 0) {
          delete message.reactions[reactionKey]
        }
      }
    } else {
      const response = await api.post(`/messages/${message.messageId}/reactions`, {
        reaction: reactionKey
      })
      if (message.userReaction && message.reactions[message.userReaction]) {
        message.reactions[message.userReaction]--
        if (message.reactions[message.userReaction] <= 0) {
          delete message.reactions[message.userReaction]
        }
      }
      message.userReaction = reactionKey
      message.reactions = message.reactions || {}
      message.reactions[reactionKey] = (message.reactions[reactionKey] || 0) + 1
    }
  } catch (error) {
    console.error('Error toggling reaction:', error)
  }
}

// Reply functions
const startReply = (message) => {
  replyingTo.value = message
  cancelEdit()
}

const cancelReply = () => {
  replyingTo.value = null
}

// ─── PASTE HANDLING ──────────────────────────────────────

const handlePaste = (event) => {
  const clipboardData = event.clipboardData
  if (!clipboardData) return

  const items = clipboardData.items
  if (!items) return

  for (let i = 0; i < items.length; i++) {
    const item = items[i]

    // Check for image (screenshot, copied image)
    if (item.type.startsWith('image/')) {
      event.preventDefault()
      const file = item.getAsFile()
      if (file) {
        showPastePreview(file, 'image')
      }
      return
    }

    // Check for files
    if (item.kind === 'file') {
      event.preventDefault()
      const file = item.getAsFile()
      if (file) {
        const type = file.type.startsWith('image/') ? 'image' : 
                     file.type.startsWith('video/') ? 'video' : 'file'
        showPastePreview(file, type)
      }
      return
    }
  }
  // If no files/images, let normal text paste happen
}

const showPastePreview = (file, type) => {
  pasteFile.value = file

  if (type === 'image') {
    const reader = new FileReader()
    reader.onload = (e) => {
      pastePreview.value = {
        type: 'image',
        name: file.name || 'pasted-image.png',
        dataUrl: e.target.result
      }
    }
    reader.readAsDataURL(file)
  } else {
    pastePreview.value = {
      type: 'file',
      name: file.name || 'pasted-file'
    }
  }
}

const clearPastePreview = () => {
  pastePreview.value = null
  pasteFile.value = null
}

const uploadPastedFile = async () => {
  if (!pasteFile.value) return

  if (pasteFile.value.size > 10 * 1024 * 1024) {
    alert(t('chat.fileTooLarge'))
    clearPastePreview()
    return
  }

  uploading.value = true
  try {
    const formData = new FormData()
    formData.append('file', pasteFile.value, pasteFile.value.name || 'pasted-image.png')

    const uploadRes = await api.post('/chat/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })

    if (!uploadRes.data.file) {
      throw new Error('No file data in response')
    }

    const fileData = uploadRes.data.file
    const caption = newMessage.value?.trim() || pasteFile.value.name || 'pasted-image.png'

    const response = await api.post(`/conversations/${props.conversation.conversationId}/messages`, {
      content: caption,
      messageType: fileData.messageType,
      fileUrl: fileData.url,
      fileName: fileData.name,
      fileSize: fileData.size,
      fileMimeType: fileData.mimeType,
      replyToId: replyingTo.value?.messageId || null
    })

    if (response.data.message) {
      messages.value.push(response.data.message)
      scrollToBottom()
      replyingTo.value = null
    }

    newMessage.value = ''
    clearPastePreview()
  } catch (error) {
    console.error('Error uploading pasted file:', error)
    alert(error.response?.data?.error || t('chat.uploadFailed'))
  } finally {
    uploading.value = false
  }
}

const isDragging = ref(false)
let dragLeaveTimeout = null

const onDragOver = (event) => {
  isDragging.value = true
  if (dragLeaveTimeout) clearTimeout(dragLeaveTimeout)
}

const onDragLeave = (event) => {
  // Small timeout to prevent flicker when dragging between child elements
  dragLeaveTimeout = setTimeout(() => {
    isDragging.value = false
  }, 100)
}

const onDrop = (event) => {
  isDragging.value = false
  if (dragLeaveTimeout) clearTimeout(dragLeaveTimeout)

  const files = event.dataTransfer?.files
  if (!files || files.length === 0) return

  const file = files[0]
  const type = file.type.startsWith('image/') ? 'image' : 
               file.type.startsWith('video/') ? 'video' : 'file'
  showPastePreview(file, type)
}

// ─── VIDEO CALL ──────────────────────────────────────────

const isVideoCallMessage = (message) => {
  return message.content?.includes('Channel: call_')
}

const extractChannelName = (content) => {
  const match = content?.match(/Channel: (call_\d+_\d+_\d+)/)
  return match ? match[1] : null
}

const startVideoCall = async () => {
  const myUserId = currentUserId.value
  const otherUserId = props.conversation.otherUser?.userId
  const channelName = `call_${myUserId}_${otherUserId}_${Date.now()}`

  try {
    const response = await api.post(`/conversations/${props.conversation.conversationId}/messages`, {
      content: `📹 ${t('chat.videoCall')}\n\nChannel: ${channelName}`
    })
    if (response.data.message) {
      messages.value.push(response.data.message)
      scrollToBottom()
    }
  } catch (error) {
    console.error('Error sending video call invite:', error)
    return
  }

  videoCallChannel.value = channelName
  isOutgoingCall.value = true
  showVideoCall.value = true
}

const joinVideoCall = (message) => {
  const channelName = extractChannelName(message.content)
  if (!channelName) return
  videoCallChannel.value = channelName
  isOutgoingCall.value = false
  showVideoCall.value = true
}

const onCallEnded = () => {
  showVideoCall.value = false
  videoCallChannel.value = ''
}

const scrollToMessage = (messageId) => {
  const element = document.querySelector(`[data-message-id="${messageId}"]`)
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'center' })
    const bubble = element.querySelector('.message-bubble, .expanded-bubble')
    if (bubble) {
      bubble.classList.add('highlight')
      setTimeout(() => bubble.classList.remove('highlight'), 2000)
    }
  }
}

const messagesContainer = ref(null)
const expandedMessagesContainer = ref(null)
const fileInput = ref(null)

let pollInterval = null
let typingTimeout = null

const currentUserId = computed(() => store.getters['auth/user']?.userId)

onlineStatus.value = props.conversation.otherUser?.onlineStatus || 'offline'

const formatTime = (dateString) => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' })
}

const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  const now = new Date()
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24))
  if (diffDays === 0) return t('chat.today')
  if (diffDays === 1) return t('chat.yesterday')
  return date.toLocaleDateString('da-DK', { day: 'numeric', month: 'short' })
}

const showDateSeparator = (index) => {
  if (index === 0) return true
  const prev = new Date(messages.value[index - 1].createdAt).toDateString()
  const curr = new Date(messages.value[index].createdAt).toDateString()
  return prev !== curr
}

const scrollToBottom = () => {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
    }
    if (expandedMessagesContainer.value) {
      expandedMessagesContainer.value.$el 
        ? expandedMessagesContainer.value.$el.scrollTop = expandedMessagesContainer.value.$el.scrollHeight
        : null
    }
  })
}

const loadMessages = async () => {
  loading.value = true
  try {
    const response = await api.get(`/conversations/${props.conversation.conversationId}/messages`)
    messages.value = response.data.messages || []
    hasMore.value = response.data.hasMore
    emit('update-unread', props.conversation.conversationId, 0)
    scrollToBottom()
  } catch (error) {
    console.error('Error loading messages:', error)
  } finally {
    loading.value = false
  }
}

const loadMore = async () => {
  if (!messages.value.length) return
  loadingMore.value = true
  try {
    const response = await api.get(`/conversations/${props.conversation.conversationId}/messages`, {
      params: { before: messages.value[0].messageId }
    })
    messages.value = [...(response.data.messages || []), ...messages.value]
    hasMore.value = response.data.hasMore
  } catch (error) {
    console.error('Error loading more:', error)
  } finally {
    loadingMore.value = false
  }
}

const sendMessage = async () => {
  // If there's a pasted file, upload it instead
  if (pasteFile.value) {
    await uploadPastedFile()
    return
  }

  if (!newMessage.value.trim()) return
  sending.value = true
  try {
    if (editingMessage.value) {
      await api.put(`/messages/${editingMessage.value.messageId}`, { content: newMessage.value })
      const idx = messages.value.findIndex(m => m.messageId === editingMessage.value.messageId)
      if (idx !== -1) {
        messages.value[idx].content = newMessage.value
        messages.value[idx].isEdited = true
      }
      editingMessage.value = null
    } else {
      const response = await api.post(`/conversations/${props.conversation.conversationId}/messages`, {
        content: newMessage.value,
        replyToId: replyingTo.value?.messageId || null
      })
      messages.value.push(response.data.message)
      scrollToBottom()
    }
    newMessage.value = ''
    replyingTo.value = null
  } catch (error) {
    console.error('Error sending:', error)
  } finally {
    sending.value = false
  }
}

const handleKeydown = (e) => {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault()
    sendMessage()
  }
  if (e.key === 'Escape') {
    if (editingMessage.value) cancelEdit()
    if (replyingTo.value) cancelReply()
    if (pastePreview.value) clearPastePreview()
  }
}

const sendTypingIndicator = () => {
  api.post(`/conversations/${props.conversation.conversationId}/typing`).catch(() => {})
  if (typingTimeout) clearTimeout(typingTimeout)
  typingTimeout = setTimeout(() => {
    api.delete(`/conversations/${props.conversation.conversationId}/typing`).catch(() => {})
  }, 3000)
}

const startEdit = (message) => {
  editingMessage.value = message
  newMessage.value = message.content
  cancelReply()
}

const cancelEdit = () => {
  editingMessage.value = null
  newMessage.value = ''
}

const deleteMessage = async (message) => {
  if (!confirm(t('chat.deleteConfirm'))) return
  try {
    await api.delete(`/messages/${message.messageId}`)
    const idx = messages.value.findIndex(m => m.messageId === message.messageId)
    if (idx !== -1) {
      messages.value[idx].isDeleted = true
      messages.value[idx].content = '[' + t('chat.messageDeleted') + ']'
    }
  } catch (error) {
    console.error('Error deleting:', error)
  }
}

const triggerFileUpload = () => fileInput.value?.click()

const handleFileUpload = async (e) => {
  const file = e.target.files?.[0]
  if (!file) return
  
  if (file.size > 10 * 1024 * 1024) {
    alert(t('chat.fileTooLarge'))
    return
  }
  
  uploading.value = true
  try {
    const formData = new FormData()
    formData.append('file', file)
    
    const uploadRes = await api.post('/chat/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    
    if (!uploadRes.data.file) {
      throw new Error('No file data in response')
    }
    
    const fileData = uploadRes.data.file
    
    const response = await api.post(`/conversations/${props.conversation.conversationId}/messages`, {
      content: file.name,
      messageType: fileData.messageType,
      fileUrl: fileData.url,
      fileName: fileData.name,
      fileSize: fileData.size,
      fileMimeType: fileData.mimeType,
      replyToId: replyingTo.value?.messageId || null
    })
    
    if (response.data.message) {
      messages.value.push(response.data.message)
      scrollToBottom()
      replyingTo.value = null
    }
  } catch (error) {
    console.error('Error uploading:', error)
    alert(error.response?.data?.error || t('chat.uploadFailed'))
  } finally {
    uploading.value = false
    e.target.value = ''
  }
}

const openFile = (url) => window.open(url, '_blank')

const startPolling = () => {
  stopPolling()
  pollInterval = setInterval(async () => {
    try {
      const lastId = messages.value.length ? messages.value[messages.value.length - 1].messageId : 0
      const response = await api.get(`/conversations/${props.conversation.conversationId}/poll`, {
        params: { lastMessageId: lastId }
      })
      if (response.data.newMessages?.length) {
        messages.value.push(...response.data.newMessages)
        scrollToBottom()
        emit('new-message', props.conversation.conversationId)
        
        const hasNewFromOther = response.data.newMessages.some(
          msg => msg.senderId !== currentUserId.value
        )
        if (hasNewFromOther) {
          notificationSound.play()
        }
      }
      if (response.data.readReceipts?.length) {
        for (const r of response.data.readReceipts) {
          const msg = messages.value.find(m => m.messageId === r.messageId)
          if (msg) msg.readAt = r.readAt
        }
      }
      isTyping.value = !!response.data.typing
      const newOnlineStatus = response.data.otherUserOnlineStatus || 'offline'
      if (onlineStatus.value !== newOnlineStatus) {
        onlineStatus.value = newOnlineStatus
        emit('online-status-change', props.conversation.conversationId, newOnlineStatus)
      }
    } catch (error) {
      console.debug('Poll error:', error)
    }
  }, 1000)
}

const stopPolling = () => {
  if (pollInterval) {
    clearInterval(pollInterval)
    pollInterval = null
  }
}

// Scroll to bottom when expanded dialog opens
watch(expanded, (val) => {
  if (val) {
    nextTick(() => {
      scrollToBottom()
    })
  }
})

onMounted(() => {
  loadMessages()
  startPolling()
})

onUnmounted(() => {
  stopPolling()
  if (typingTimeout) clearTimeout(typingTimeout)
})

watch(() => props.minimized, (val) => {
  if (!val) {
    scrollToBottom()
  }
})
</script>

<style scoped>
.chat-window {
  position: fixed;
  bottom: 0;
  width: 320px;
  max-height: 450px;
  background: white;
  border-radius: 12px 12px 0 0;
  box-shadow: 0 -2px 20px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  z-index: 1000;
  overflow: hidden;
}

.chat-window-header {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  background: rgb(var(--v-theme-primary));
  color: white;
  cursor: pointer;
}

.header-info {
  flex: 1;
  min-width: 0;
}

.header-name {
  font-weight: 500;
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.header-status {
  font-size: 11px;
  opacity: 0.9;
}

.typing-text { font-style: italic; }
.status-online { color: #81C784; }
.status-away { color: #FFD54F; }
.status-offline { opacity: 0.7; }

.header-actions {
  display: flex;
  gap: 2px;
}

.header-actions .v-btn {
  color: white !important;
}

.chat-window-messages {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  background: #f5f5f5;
  max-height: 300px;
  min-height: 200px;
}

.message-wrapper {
  margin-bottom: 8px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.message-wrapper.own-message {
  align-items: flex-end;
}

.message-wrapper.own-message .message-bubble {
  background: rgba(var(--v-theme-primary), 0.15);
}

.message-bubble {
  background: white;
  padding: 8px 10px;
  border-radius: 12px;
  max-width: 85%;
  min-width: 80px;
  position: relative;
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
}

.message-bubble.deleted {
  opacity: 0.6;
  font-style: italic;
}

.message-bubble.highlight {
  animation: highlight-pulse 2s ease-out;
}

@keyframes highlight-pulse {
  0% { box-shadow: 0 0 0 4px rgba(74, 139, 127, 0.5); }
  100% { box-shadow: 0 1px 2px rgba(0,0,0,0.1); }
}

.message-text {
  font-size: 13px;
  word-wrap: break-word;
  white-space: pre-wrap;
}

.message-meta {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 4px;
  font-size: 10px;
  color: #888;
}

.edited-badge {
  font-style: italic;
  margin-left: 4px;
}

.message-image {
  max-width: 200px;
  max-height: 150px;
  border-radius: 8px;
  cursor: pointer;
  margin-bottom: 4px;
}

.file-attachment {
  display: flex;
  align-items: center;
  padding: 6px 8px;
  background: rgba(0,0,0,0.05);
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  margin-bottom: 4px;
}

.video-call-invite {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  background: rgba(74, 139, 127, 0.08);
  border-radius: 8px;
  margin-bottom: 4px;
  border: 1px solid rgba(74, 139, 127, 0.2);
}

.video-call-label {
  font-size: 13px;
  font-weight: 500;
  color: rgb(var(--v-theme-primary));
}

.message-actions {
  position: absolute;
  top: 2px;
  right: 2px;
  display: none;
}

.message-bubble:hover .message-actions {
  display: flex;
}

/* Reply quote styles */
.reply-quote {
  padding: 6px 8px;
  margin-bottom: 6px;
  background: rgba(0, 0, 0, 0.05);
  border-left: 2px solid #4A8B7F;
  border-radius: 0 4px 4px 0;
  cursor: pointer;
  transition: background 0.15s;
}

.reply-quote:hover {
  background: rgba(0, 0, 0, 0.08);
}

.own-message .reply-quote {
  background: rgba(255, 255, 255, 0.3);
  border-left-color: rgba(74, 139, 127, 0.7);
}

.reply-quote-name {
  font-size: 11px;
  font-weight: 600;
  color: #4A8B7F;
}

.reply-quote-text {
  font-size: 11px;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
}

.reply-preview {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: rgba(74, 139, 127, 0.1);
  border-left: 3px solid #4A8B7F;
  margin-bottom: 8px;
  border-radius: 0 6px 6px 0;
}

.reply-preview-content {
  flex: 1;
  min-width: 0;
}

.reply-preview-name {
  font-size: 12px;
  font-weight: 600;
  color: #4A8B7F;
}

.reply-preview-text {
  font-size: 12px;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Reaction picker */
.reaction-picker {
  position: absolute;
  bottom: -8px;
  left: 0;
  display: none;
  background: white;
  border-radius: 20px;
  padding: 4px 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  gap: 4px;
  z-index: 10;
  white-space: nowrap;
}

.message-wrapper.own-message .reaction-picker {
  left: auto;
  right: 0;
}

.message-bubble:hover .reaction-picker {
  display: flex;
}

.reaction-btn {
  border: none;
  background: none;
  font-size: 16px;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 4px;
  transition: transform 0.15s, background 0.15s;
}

.reaction-btn:hover {
  transform: scale(1.3);
  background: rgba(0,0,0,0.05);
}

.reaction-btn.active {
  background: rgba(74, 139, 127, 0.2);
}

.reply-btn {
  border-left: 1px solid #ddd;
  margin-left: 4px;
  padding-left: 8px;
}

.message-reactions {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}

.reaction-badge {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px 6px;
  background: rgba(0,0,0,0.05);
  border-radius: 10px;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.15s;
}

.reaction-badge:hover {
  background: rgba(0,0,0,0.1);
}

.reaction-badge.user-reacted {
  background: rgba(74, 139, 127, 0.2);
  border: 1px solid rgba(74, 139, 127, 0.3);
}

.date-separator {
  text-align: center;
  margin: 12px 0 8px;
  font-size: 11px;
  color: #888;
}

.chat-window-input {
  padding: 8px 12px;
  background: white;
  border-top: 1px solid #eee;
}

.edit-indicator {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  background: rgba(74, 139, 127, 0.1);
  border-radius: 6px;
  margin-bottom: 8px;
  font-size: 12px;
  color: #4A8B7F;
}

.input-row {
  display: flex;
  align-items: flex-end;
  gap: 4px;
}

.message-input {
  font-size: 14px;
}

.message-input :deep(.v-field__input) {
  padding: 8px 0;
  min-height: 36px;
}

/* ─── PASTE PREVIEW ─────────────────────────────────── */

.paste-preview {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  margin-bottom: 8px;
  background: #f0f7f5;
  border: 1px solid #c8e6c9;
  border-radius: 8px;
  position: relative;
}

.paste-preview-image {
  max-width: 80px;
  max-height: 60px;
  border-radius: 6px;
  object-fit: cover;
}

.paste-preview-file {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #555;
}

.paste-preview-close {
  position: absolute;
  top: 2px;
  right: 2px;
}

/* ═══════════════════════════════════════════════════════ */
/* EXPANDED DIALOG STYLES (large mode)                    */
/* ═══════════════════════════════════════════════════════ */

.expanded-chat {
  border-radius: 16px !important;
  overflow: hidden;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.expanded-header {
  background: rgb(var(--v-theme-primary));
  color: white;
  padding: 16px 20px !important;
}

.expanded-header .v-btn {
  color: white !important;
}

.expanded-header-name {
  font-size: 22px;
  font-weight: 600;
}

.expanded-header-status {
  font-size: 16px;
  opacity: 0.9;
}

.expanded-messages {
  flex: 1;
  overflow-y: auto;
  background: #f5f5f5;
  padding: 20px 24px !important;
  min-height: 400px;
  max-height: 60vh;
}

.expanded-message-wrapper {
  margin-bottom: 14px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.expanded-message-wrapper.own-message {
  align-items: flex-end;
}

.expanded-message-wrapper.own-message .expanded-bubble {
  background: rgba(var(--v-theme-primary), 0.15);
}

.expanded-bubble {
  background: white;
  padding: 14px 18px;
  border-radius: 16px;
  max-width: 75%;
  min-width: 120px;
  position: relative;
  box-shadow: 0 2px 6px rgba(0,0,0,0.08);
}

.expanded-bubble.deleted {
  opacity: 0.6;
  font-style: italic;
}

.expanded-bubble.highlight {
  animation: highlight-pulse 2s ease-out;
}

.expanded-reply-quote {
  padding: 8px 12px;
  margin-bottom: 8px;
  background: rgba(0, 0, 0, 0.05);
  border-left: 3px solid #4A8B7F;
  border-radius: 0 6px 6px 0;
  cursor: pointer;
}

.expanded-reply-quote .reply-quote-name {
  font-size: 14px;
}

.expanded-reply-quote .reply-quote-text {
  font-size: 14px;
  max-width: 400px;
}

.expanded-message-text {
  font-size: 20px;
  line-height: 1.5;
  word-wrap: break-word;
  white-space: pre-wrap;
}

.expanded-message-meta {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 6px;
  font-size: 14px;
  color: #888;
  gap: 6px;
}

.expanded-message-image {
  max-width: 400px;
  max-height: 300px;
  border-radius: 12px;
  cursor: pointer;
  margin-bottom: 6px;
}

.expanded-file-attachment {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  background: rgba(0,0,0,0.05);
  border-radius: 8px;
  font-size: 16px;
  cursor: pointer;
  margin-bottom: 6px;
}

.expanded-video-call {
  padding: 14px 18px;
}

.expanded-video-call .video-call-label {
  font-size: 18px;
}

.expanded-reactions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.expanded-reaction-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: rgba(0,0,0,0.05);
  border-radius: 14px;
  font-size: 18px;
  cursor: pointer;
  transition: background 0.15s;
}

.expanded-reaction-badge:hover {
  background: rgba(0,0,0,0.1);
}

.expanded-reaction-badge.user-reacted {
  background: rgba(74, 139, 127, 0.2);
  border: 1px solid rgba(74, 139, 127, 0.3);
}

.expanded-reaction-picker {
  position: absolute;
  bottom: -12px;
  left: 0;
  display: none;
  background: white;
  border-radius: 24px;
  padding: 6px 12px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.15);
  gap: 6px;
  z-index: 10;
  white-space: nowrap;
}

.expanded-message-wrapper.own-message .expanded-reaction-picker {
  left: auto;
  right: 0;
}

.expanded-bubble:hover .expanded-reaction-picker {
  display: flex;
}

.expanded-react-btn {
  border: none;
  background: none;
  font-size: 24px;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 6px;
  transition: transform 0.15s, background 0.15s;
}

.expanded-react-btn:hover {
  transform: scale(1.3);
  background: rgba(0,0,0,0.05);
}

.expanded-react-btn.active {
  background: rgba(74, 139, 127, 0.2);
}

.expanded-react-btn.reply-btn {
  border-left: 1px solid #ddd;
  margin-left: 6px;
  padding-left: 12px;
}

.expanded-date-separator {
  text-align: center;
  margin: 16px 0 12px;
  font-size: 16px;
  color: #888;
  font-weight: 500;
}

/* Expanded input area */
.expanded-input-area {
  flex-direction: column;
  padding: 12px 20px 16px !important;
  border-top: 1px solid #eee;
  background: white;
}

.expanded-paste-preview {
  width: 100%;
  margin-bottom: 12px;
}

.paste-preview-image-lg {
  max-width: 120px;
  max-height: 90px;
  border-radius: 8px;
  object-fit: cover;
}

.expanded-reply-preview {
  width: 100%;
  margin-bottom: 12px;
  font-size: 15px;
}

.expanded-reply-preview .reply-preview-name {
  font-size: 15px;
}

.expanded-reply-preview .reply-preview-text {
  font-size: 15px;
}

.expanded-input-row {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  width: 100%;
}

.expanded-message-input {
  flex: 1;
  font-size: 18px;
}

.expanded-message-input :deep(.v-field__input) {
  font-size: 18px;
  line-height: 1.5;
}

.drop-active {
  outline: 3px dashed #4A8B7F !important;
  outline-offset: -3px;
  background: rgba(74, 139, 127, 0.08) !important;
}

.drop-active::after {
  content: '📎';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 48px;
  opacity: 0.3;
  pointer-events: none;
}

/* ─── RESPONSIVE ────────────────────────────────────── */

@media (max-width: 600px) {
  .expanded-chat {
    max-height: 100vh;
    border-radius: 0 !important;
  }
  
  .expanded-message-text {
    font-size: 18px;
  }
  
  .expanded-message-input :deep(.v-field__input) {
    font-size: 16px;
  }
}
</style>